/**
 * PR-D4-036 — Inactivity auto-logout (60 minutes by default).
 * Completely disabled when `enabled` is false (active test routes).
 */

import { useEffect, useRef } from 'react';

const ACTIVITY_EVENTS = [
    'mousemove',
    'keydown',
    'click',
    'scroll',
    'touchstart',
] as const;

export interface UseInactivityTimerOptions {
    timeoutMs?: number;
    enabled?: boolean;
    onTimeout: () => void;
}

export function useInactivityTimer({
    timeoutMs = 60 * 60 * 1000,
    enabled = true,
    onTimeout,
}: UseInactivityTimerOptions): void {
    const onTimeoutRef = useRef(onTimeout);
    onTimeoutRef.current = onTimeout;

    useEffect(() => {
        if (!enabled) return;

        let timerId: ReturnType<typeof setTimeout> | null = null;
        let fired = false;

        const clear = () => {
            if (timerId !== null) {
                clearTimeout(timerId);
                timerId = null;
            }
        };

        const arm = () => {
            clear();
            timerId = setTimeout(() => {
                if (fired) return;
                fired = true;
                onTimeoutRef.current();
            }, timeoutMs);
        };

        const onActivity = () => {
            if (fired) return;
            arm();
        };

        arm();

        for (const eventName of ACTIVITY_EVENTS) {
            window.addEventListener(eventName, onActivity, { passive: true });
        }

        return () => {
            clear();
            for (const eventName of ACTIVITY_EVENTS) {
                window.removeEventListener(eventName, onActivity);
            }
        };
    }, [enabled, timeoutMs]);
}
